import React, { useMemo } from 'react'
import { AgendaItem, AgendaItemStatus } from '../../../../../models/Agenda'
import { ReactComponent as CompletedIcon } from '../../../../../assets/images/agenda-item-status-completed.svg'
import { ReactComponent as OpenIcon } from '../../../../../assets/images/agenda-item-status-open.svg'
import { ProgressCircle } from '../../../../../components/ProgressCircle/ProgressCircle'

interface LiveMeetingProps {
  items: AgendaItem[]
  onToggleComplete: (itemId: string, newStatus: AgendaItemStatus) => void
}

interface AgendaTrackerProps {
  completedCount: number
  totalCount: number
}

const AgendaTracker: React.FC<AgendaTrackerProps> = ({ completedCount, totalCount }) => {
  const percentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0

  const trackerMessage =
    percentage === 100 ? 'All agenda items were covered. Great job staying on track!' : `${completedCount} of ${totalCount} agenda items were covered.`

  return (
    <div className='bg-white border zocks-border-gray-300 rounded zocks-p-0.5 mb-3'>
      <div className='bg-light border zocks-border-gray-200 rounded d-flex align-items-center zocks-px-0.75 zocks-py-0.375 m-1'>
        <div className='d-flex align-items-center zocks-gap-1 flex-grow-1'>
          <div className='flex-shrink-0 d-flex align-items-center zocks-pr-3'>
            <ProgressCircle percentage={percentage} />
          </div>
          <div className='flex-grow-1'>
            <p className='paragraph-medium zocks-text-gray-900 mb-0'>{trackerMessage}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

interface AgendaItemRowProps {
  item: AgendaItem
  onToggle: (itemId: string, newStatus: AgendaItemStatus) => void
}

const AgendaItemRow: React.FC<AgendaItemRowProps> = ({ item, onToggle }) => {
  const isChecked = item.status === AgendaItemStatus.Completed

  const handleToggle = () => {
    const newStatus = isChecked ? AgendaItemStatus.Open : AgendaItemStatus.Completed
    onToggle(item.id, newStatus)
  }

  return (
    <div className='border zocks-border-gray-200 rounded d-flex align-items-center zocks-px-1 zocks-py-0.75 mb-2'>
      <div className='d-flex align-items-center flex-grow-1 zocks-gap-0.75 zocks-px-0.75'>
        <div className='flex-shrink-0'>
          {isChecked ? <CompletedIcon onClick={handleToggle} className='cursor-pointer' /> : <OpenIcon onClick={handleToggle} className='cursor-pointer' />}
        </div>
        <label
          htmlFor={`agenda-item-${item.id}`}
          className={`paragraph-medium-extended mb-0 flex-grow-1 cursor-pointer ${
            isChecked ? 'text-decoration-line-through zocks-text-gray-700' : 'text-decoration-none zocks-text-gray-900'
          }`}
          onClick={handleToggle}
        >
          {item.text}
        </label>
      </div>
    </div>
  )
}

export const LiveMeetingView: React.FC<LiveMeetingProps> = ({ items, onToggleComplete }) => {
  const completedCount = useMemo(() => {
    return items.filter((item) => item.status === AgendaItemStatus.Completed).length
  }, [items])

  return (
    <div className='zocks-px-1 py-0'>
      <AgendaTracker completedCount={completedCount} totalCount={items.length} />
      {items.map((item) => (
        <AgendaItemRow key={item.id} item={item} onToggle={onToggleComplete} />
      ))}
    </div>
  )
}

export default LiveMeetingView
